import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import CookieBanner from '../../components/feature/CookieBanner';
import ScrollToTopButton from '../../components/feature/ScrollToTopButton';
import OptimizedImage from '../../components/base/OptimizedImage';
import Breadcrumbs from '../../components/feature/Breadcrumbs';

const ServiciosPage = () => {
  useEffect(() => {
    document.title = 'Servicios de Pladur en Sabadell | PLADUREX - Tabiques, Techos, Aislamientos';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Servicios de instalación de pladur en Sabadell: tabiques, trasdosados, falsos techos, aislamiento acústico y térmico, pladur antihumedad. Presupuesto personalizado ☎ 629 501 941');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'servicios pladur Sabadell, tabiques pladur, falsos techos, trasdosados, aislamiento acústico Sabadell');
    }

    // Schema.org JSON-LD
    const servicesSchema = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "itemListElement": [
        {
          "@type": "Service",
          "position": 1,
          "name": "Tabiques de Pladur",
          "description": "Divisiones interiores rápidas y limpias para redistribuir espacios",
          "provider": {
            "@type": "LocalBusiness",
            "name": "PLADUREX"
          },
          "areaServed": {
            "@type": "City",
            "name": "Sabadell"
          }
        },
        {
          "@type": "Service",
          "position": 2,
          "name": "Trasdosados",
          "description": "Directos y autoportantes con aislamiento térmico y acústico",
          "provider": {
            "@type": "LocalBusiness",
            "name": "PLADUREX"
          },
          "areaServed": {
            "@type": "City",
            "name": "Sabadell"
          }
        },
        {
          "@type": "Service",
          "position": 3,
          "name": "Falsos Techos",
          "description": "Techos continuos y registrables que mejoran estética y funcionalidad",
          "provider": {
            "@type": "LocalBusiness",
            "name": "PLADUREX"
          },
          "areaServed": {
            "@type": "City",
            "name": "Sabadell"
          }
        },
        {
          "@type": "Service",
          "position": 4,
          "name": "Aislamiento Acústico",
          "description": "Soluciones de insonorización para reducir el ruido",
          "provider": {
            "@type": "LocalBusiness",
            "name": "PLADUREX"
          },
          "areaServed": {
            "@type": "City",
            "name": "Sabadell"
          }
        },
        {
          "@type": "Service",
          "position": 5,
          "name": "Aislamiento Térmico",
          "description": "Mejora la eficiencia energética y el confort",
          "provider": {
            "@type": "LocalBusiness",
            "name": "PLADUREX"
          },
          "areaServed": {
            "@type": "City",
            "name": "Sabadell"
          }
        },
        {
          "@type": "Service",
          "position": 6,
          "name": "Pladur Antihumedad",
          "description": "Placas especiales resistentes a la humedad",
          "provider": {
            "@type": "LocalBusiness",
            "name": "PLADUREX"
          },
          "areaServed": {
            "@type": "City",
            "name": "Sabadell"
          }
        }
      ]
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(servicesSchema);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const services = [
    {
      id: 'tabiques',
      icon: 'ri-layout-grid-line',
      title: 'Tabiques de Pladur',
      description: 'Divisiones interiores rápidas y limpias para redistribuir espacios',
      image: 'https://public.readdy.ai/ai/img_res/bfccd87727921dcab1eb01edd822fa3e.jpg',
      includes: [
        'Estructura metálica de perfiles',
        'Placas de yeso laminado estándar o especiales',
        'Tratamiento de juntas y acabado liso',
        'Opción de aislamiento acústico interior',
        'Preparación para pintura o revestimiento'
      ],
      useCases: [
        'Crear dormitorios adicionales en viviendas',
        'Separar zonas en oficinas diáfanas',
        'Dividir locales comerciales',
        'Cerrar terrazas o balcones',
        'Crear vestidores o zonas de almacenaje'
      ],
      priceFactors: [
        'Metros cuadrados de tabique',
        'Altura del techo',
        'Tipo de placa (estándar, hidrófuga, ignífuga)',
        'Aislamiento acústico o térmico',
        'Número de puertas o pasos',
        'Accesibilidad de la zona'
      ]
    },
    {
      id: 'trasdosados',
      icon: 'ri-building-line',
      title: 'Trasdosados',
      description: 'Directos y autoportantes con aislamiento térmico y acústico',
      image: 'https://readdy.ai/api/search-image?query=interior%20wall%20insulation%20drywall%20backing%20system%20clean%20installation%20thermal%20acoustic%20insulation%20professional%20construction%20minimalist%20background%20high%20quality&width=800&height=600&seq=service-trasdosados-004&orientation=landscape',
      includes: [
        'Trasdosado directo sobre pared existente',
        'Trasdosado autoportante con estructura',
        'Aislamiento térmico con lana mineral',
        'Aislamiento acústico según necesidades',
        'Acabado liso listo para pintar'
      ],
      useCases: [
        'Mejorar aislamiento térmico de fachadas',
        'Reducir ruido de vecinos colindantes',
        'Ocultar instalaciones en paredes',
        'Regularizar paredes irregulares',
        'Ganar confort en viviendas antiguas'
      ],
      priceFactors: [
        'Metros cuadrados de superficie',
        'Tipo de trasdosado (directo o autoportante)',
        'Grosor y tipo de aislamiento',
        'Estado de la pared existente',
        'Altura de la estancia',
        'Complejidad de la instalación'
      ]
    },
    {
      id: 'techos',
      icon: 'ri-stack-line',
      title: 'Falsos Techos',
      description: 'Techos continuos y registrables que mejoran estética y funcionalidad',
      image: 'https://readdy.ai/api/search-image?query=modern%20suspended%20ceiling%20drywall%20clean%20white%20surface%20minimalist%20interior%20lighting%20installation%20professional%20construction%20high%20quality%20architectural%20photography&width=800&height=600&seq=service-techos-005&orientation=landscape',
      includes: [
        'Techo continuo de pladur',
        'Techo registrable desmontable',
        'Estructura de perfiles metálicos',
        'Integración de iluminación',
        'Aislamiento térmico y acústico opcional'
      ],
      useCases: [
        'Ocultar vigas y instalaciones',
        'Bajar altura de techos muy altos',
        'Mejorar aislamiento térmico superior',
        'Integrar iluminación empotrada',
        'Crear diseños con niveles y formas',
        'Facilitar acceso a instalaciones (registrable)'
      ],
      priceFactors: [
        'Metros cuadrados de techo',
        'Tipo de techo (continuo o registrable)',
        'Altura de la sala',
        'Número de puntos de luz',
        'Formas especiales o niveles',
        'Aislamiento incluido'
      ]
    },
    {
      id: 'acustico',
      icon: 'ri-volume-mute-line',
      title: 'Aislamiento Acústico',
      description: 'Soluciones de insonorización para reducir el ruido',
      image: 'https://readdy.ai/api/search-image?query=acoustic%20insulation%20material%20soundproofing%20panels%20mineral%20wool%20clean%20installation%20professional%20construction%20minimalist%20background%20high%20quality&width=800&height=600&seq=service-acustico-006&orientation=landscape',
      includes: [
        'Lana mineral de alta densidad',
        'Placas de yeso laminado acústicas',
        'Sistema de doble placa',
        'Banda elástica perimetral',
        'Sellado acústico de juntas'
      ],
      useCases: [
        'Reducir ruido entre viviendas',
        'Insonorizar dormitorios',
        'Aislar salas de música o estudio',
        'Mejorar privacidad en oficinas',
        'Reducir ruido de locales comerciales'
      ],
      priceFactors: [
        'Superficie a aislar',
        'Nivel de aislamiento requerido',
        'Tipo de lana acústica',
        'Sistema de doble o triple placa',
        'Tratamiento de suelos y techos',
        'Complejidad de la instalación'
      ]
    },
    {
      id: 'termico',
      icon: 'ri-temp-cold-line',
      title: 'Aislamiento Térmico',
      description: 'Mejora la eficiencia energética y el confort',
      image: 'https://readdy.ai/api/search-image?query=thermal%20insulation%20material%20mineral%20wool%20installation%20energy%20efficiency%20clean%20professional%20construction%20minimalist%20background%20high%20quality&width=800&height=600&seq=service-termico-007&orientation=landscape',
      includes: [
        'Lana mineral o poliestireno',
        'Trasdosado con aislamiento',
        'Sellado de puentes térmicos',
        'Mejora de eficiencia energética',
        'Reducción de consumo de calefacción'
      ],
      useCases: [
        'Aislar fachadas exteriores',
        'Mejorar confort en invierno y verano',
        'Reducir facturas de energía',
        'Eliminar condensaciones y humedades',
        'Cumplir normativa de eficiencia'
      ],
      priceFactors: [
        'Metros cuadrados a aislar',
        'Tipo de aislante (lana, poliestireno, etc.)',
        'Grosor del aislamiento',
        'Zona a aislar (fachada, techo, suelo)',
        'Estado de la superficie existente',
        'Complejidad del trabajo'
      ]
    },
    {
      id: 'antihumedad',
      icon: 'ri-drop-line',
      title: 'Pladur Antihumedad',
      description: 'Placas especiales resistentes a la humedad',
      image: 'https://readdy.ai/api/search-image?query=moisture%20resistant%20drywall%20green%20board%20bathroom%20installation%20water%20resistant%20material%20clean%20professional%20construction%20minimalist%20background%20high%20quality&width=800&height=600&seq=service-antihumedad-008&orientation=landscape',
      includes: [
        'Placas hidrófugas (verdes)',
        'Tratamiento especial anti-moho',
        'Resistencia a ambientes húmedos',
        'Ideal para zonas de agua',
        'Acabado listo para alicatar o pintar'
      ],
      useCases: [
        'Baños y aseos',
        'Cocinas',
        'Lavaderos y zonas de lavado',
        'Sótanos con humedad',
        'Locales con ambiente húmedo'
      ],
      priceFactors: [
        'Metros cuadrados de superficie',
        'Tipo de placa hidrófuga',
        'Tratamiento adicional anti-moho',
        'Zona de aplicación',
        'Acabado posterior (alicatado, pintura)',
        'Complejidad de la instalación'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-dark-bg text-light-text">
      <Header />
      <Breadcrumbs />
      <WhatsAppButton />

      {/* Hero Section */}
      <section className="relative min-h-[50vh] flex items-center justify-center overflow-hidden pt-20">
        <div className="absolute inset-0 z-0">
          <OptimizedImage
            src="https://readdy.ai/api/search-image?query=professional%20drywall%20construction%20tools%20materials%20clean%20organized%20workspace%20modern%20interior%20minimalist%20background%20high%20quality%20architectural%20photography&width=1920&height=1080&seq=servicios-hero-009&orientation=landscape"
            alt="Servicios de pladur"
            loading="lazy"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-dark-bg"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-6xl font-display font-bold mb-6">
              Servicios de <span className="text-primary-orange">Pladur en Sabadell</span>
            </h1>
            <p className="text-xl md:text-2xl text-light-muted max-w-3xl mx-auto">
              Soluciones completas para tu proyecto de reforma o construcción
            </p>
          </motion.div>
        </div>
      </section>

      {/* Projects Gallery Section */}
      <section className="py-20 bg-dark-card">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-display font-bold mb-4">
              Proyectos <span className="text-primary-orange">realizados</span>
            </h2>
            <p className="text-lg text-light-muted max-w-2xl mx-auto">
              Ejemplos de trabajos de instalación de pladur en Sabadell y alrededores
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Project 1 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="group relative overflow-hidden rounded-xl aspect-[4/3]"
            >
              <OptimizedImage
                src="https://readdy.ai/api/search-image?query=modern%20living%20room%20interior%20with%20smooth%20white%20drywall%20walls%20clean%20minimalist%20design%20professional%20finish%20natural%20lighting%20contemporary%20furniture%20neutral%20tones%20high%20quality%20architectural%20photography&width=600&height=450&seq=project-living-001&orientation=landscape"
                alt="Tabiques de pladur en salón"
                loading="lazy"
                className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <span className="text-primary-orange text-sm font-medium">Tabiques</span>
                  <h3 className="text-xl font-semibold text-white mt-1">Redistribución de salón</h3>
                  <p className="text-light-muted text-sm mt-2">Creación de espacio de trabajo integrado en vivienda</p>
                </div>
              </div>
            </motion.div>

            {/* Project 2 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="group relative overflow-hidden rounded-xl aspect-[4/3]"
            >
              <OptimizedImage
                src="https://readdy.ai/api/search-image?query=modern%20suspended%20ceiling%20with%20recessed%20LED%20lighting%20clean%20white%20drywall%20finish%20contemporary%20interior%20design%20professional%20installation%20minimalist%20style%20high%20quality%20architectural%20photography&width=600&height=450&seq=project-ceiling-002&orientation=landscape"
                alt="Falso techo con iluminación LED"
                loading="lazy"
                className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <span className="text-primary-orange text-sm font-medium">Falsos techos</span>
                  <h3 className="text-xl font-semibold text-white mt-1">Techo con iluminación integrada</h3>
                  <p className="text-light-muted text-sm mt-2">Instalación de techo continuo con focos LED empotrados</p>
                </div>
              </div>
            </motion.div>

            {/* Project 3 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="group relative overflow-hidden rounded-xl aspect-[4/3]"
            >
              <OptimizedImage
                src="https://readdy.ai/api/search-image?query=modern%20bathroom%20interior%20with%20moisture%20resistant%20drywall%20walls%20clean%20white%20tiles%20contemporary%20design%20professional%20finish%20bright%20lighting%20minimalist%20style%20high%20quality%20architectural%20photography&width=600&height=450&seq=project-bathroom-003&orientation=landscape"
                alt="Pladur antihumedad en baño"
                loading="lazy"
                className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <span className="text-primary-orange text-sm font-medium">Antihumedad</span>
                  <h3 className="text-xl font-semibold text-white mt-1">Reforma de baño completa</h3>
                  <p className="text-light-muted text-sm mt-2">Placas hidrófugas para zona de ducha y lavabo</p>
                </div>
              </div>
            </motion.div>

            {/* Project 4 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="group relative overflow-hidden rounded-xl aspect-[4/3]"
            >
              <OptimizedImage
                src="https://readdy.ai/api/search-image?query=modern%20open%20plan%20office%20interior%20with%20drywall%20partition%20walls%20glass%20panels%20clean%20white%20surfaces%20professional%20workspace%20design%20natural%20lighting%20minimalist%20contemporary%20style%20high%20quality%20architectural%20photography&width=600&height=450&seq=project-office-004&orientation=landscape"
                alt="División de oficina con pladur"
                loading="lazy"
                className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <span className="text-primary-orange text-sm font-medium">Tabiques</span>
                  <h3 className="text-xl font-semibold text-white mt-1">División de oficina diáfana</h3>
                  <p className="text-light-muted text-sm mt-2">Creación de despachos y salas de reuniones</p>
                </div>
              </div>
            </motion.div>

            {/* Project 5 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
              className="group relative overflow-hidden rounded-xl aspect-[4/3]"
            >
              <OptimizedImage
                src="https://readdy.ai/api/search-image?query=modern%20bedroom%20interior%20with%20acoustic%20insulation%20drywall%20walls%20soundproof%20design%20clean%20white%20surfaces%20cozy%20atmosphere%20soft%20lighting%20minimalist%20contemporary%20style%20high%20quality%20architectural%20photography&width=600&height=450&seq=project-bedroom-005&orientation=landscape"
                alt="Aislamiento acústico en dormitorio"
                loading="lazy"
                className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <span className="text-primary-orange text-sm font-medium">Aislamiento acústico</span>
                  <h3 className="text-xl font-semibold text-white mt-1">Insonorización de dormitorio</h3>
                  <p className="text-light-muted text-sm mt-2">Trasdosado con lana mineral para reducir ruido</p>
                </div>
              </div>
            </motion.div>

            {/* Project 6 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6 }}
              className="group relative overflow-hidden rounded-xl aspect-[4/3]"
            >
              <OptimizedImage
                src="https://readdy.ai/api/search-image?query=modern%20walk%20in%20closet%20interior%20with%20drywall%20walls%20built%20in%20shelving%20clean%20white%20surfaces%20organized%20storage%20space%20soft%20lighting%20minimalist%20contemporary%20design%20high%20quality%20architectural%20photography&width=600&height=450&seq=project-closet-006&orientation=landscape"
                alt="Vestidor con pladur"
                loading="lazy"
                className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <span className="text-primary-orange text-sm font-medium">Tabiques</span>
                  <h3 className="text-xl font-semibold text-white mt-1">Creación de vestidor</h3>
                  <p className="text-light-muted text-sm mt-2">Aprovechamiento de espacio con tabiquería ligera</p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Before/After Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-16"
          >
            <h3 className="text-2xl font-display font-bold text-center mb-8">
              Transformaciones <span className="text-primary-orange">antes y después</span>
            </h3>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Before/After 1 */}
              <div className="bg-dark-bg rounded-xl overflow-hidden border border-dark-lighter">
                <div className="grid grid-cols-2">
                  <div className="relative aspect-[4/3]">
                    <OptimizedImage
                      src="https://readdy.ai/api/search-image?query=empty%20room%20under%20construction%20exposed%20brick%20walls%20unfinished%20interior%20renovation%20project%20raw%20space%20before%20renovation%20dusty%20floor%20construction%20site%20high%20quality%20photography&width=400&height=300&seq=before-room-001&orientation=landscape"
                      alt="Antes de la reforma"
                      loading="lazy"
                      className="w-full h-full object-cover object-top"
                    />
                    <div className="absolute top-3 left-3 px-3 py-1 bg-dark-bg/90 rounded-full text-sm font-medium">
                      Antes
                    </div>
                  </div>
                  <div className="relative aspect-[4/3]">
                    <OptimizedImage
                      src="https://readdy.ai/api/search-image?query=modern%20finished%20room%20with%20smooth%20white%20drywall%20walls%20clean%20interior%20design%20professional%20finish%20bright%20natural%20lighting%20contemporary%20minimalist%20style%20after%20renovation%20high%20quality%20photography&width=400&height=300&seq=after-room-001&orientation=landscape"
                      alt="Después de la reforma"
                      loading="lazy"
                      className="w-full h-full object-cover object-top"
                    />
                    <div className="absolute top-3 left-3 px-3 py-1 bg-primary-orange rounded-full text-sm font-medium text-white">
                      Después
                    </div>
                  </div>
                </div>
                <div className="p-5">
                  <h4 className="font-semibold mb-2">Reforma integral de vivienda</h4>
                  <p className="text-light-muted text-sm">Tabiques, trasdosados y falso techo en piso de Sabadell Centro</p>
                </div>
              </div>

              {/* Before/After 2 */}
              <div className="bg-dark-bg rounded-xl overflow-hidden border border-dark-lighter">
                <div className="grid grid-cols-2">
                  <div className="relative aspect-[4/3]">
                    <OptimizedImage
                      src="https://readdy.ai/api/search-image?query=old%20ceiling%20with%20exposed%20pipes%20and%20wires%20unfinished%20industrial%20look%20renovation%20project%20before%20installation%20raw%20concrete%20ceiling%20construction%20site%20high%20quality%20photography&width=400&height=300&seq=before-ceiling-002&orientation=landscape"
                      alt="Antes del falso techo"
                      loading="lazy"
                      className="w-full h-full object-cover object-top"
                    />
                    <div className="absolute top-3 left-3 px-3 py-1 bg-dark-bg/90 rounded-full text-sm font-medium">
                      Antes
                    </div>
                  </div>
                  <div className="relative aspect-[4/3]">
                    <OptimizedImage
                      src="https://readdy.ai/api/search-image?query=modern%20suspended%20ceiling%20with%20hidden%20lighting%20clean%20white%20drywall%20finish%20professional%20installation%20contemporary%20design%20recessed%20spotlights%20after%20renovation%20high%20quality%20photography&width=400&height=300&seq=after-ceiling-002&orientation=landscape"
                      alt="Después del falso techo"
                      loading="lazy"
                      className="w-full h-full object-cover object-top"
                    />
                    <div className="absolute top-3 left-3 px-3 py-1 bg-primary-orange rounded-full text-sm font-medium text-white">
                      Después
                    </div>
                  </div>
                </div>
                <div className="p-5">
                  <h4 className="font-semibold mb-2">Instalación de falso techo</h4>
                  <p className="text-light-muted text-sm">Ocultación de instalaciones e integración de iluminación LED</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6"
          >
            <div className="text-center p-6 bg-dark-bg rounded-xl border border-dark-lighter">
              <div className="text-4xl font-display font-bold text-primary-orange mb-2">+500</div>
              <div className="text-light-muted text-sm">Proyectos realizados</div>
            </div>
            <div className="text-center p-6 bg-dark-bg rounded-xl border border-dark-lighter">
              <div className="text-4xl font-display font-bold text-primary-orange mb-2">100%</div>
              <div className="text-light-muted text-sm">Clientes satisfechos</div>
            </div>
            <div className="text-center p-6 bg-dark-bg rounded-xl border border-dark-lighter">
              <div className="text-4xl font-display font-bold text-primary-orange mb-2">+10k</div>
              <div className="text-light-muted text-sm">m² instalados</div>
            </div>
            <div className="text-center p-6 bg-dark-bg rounded-xl border border-dark-lighter">
              <div className="text-4xl font-display font-bold text-primary-orange mb-2">48h</div>
              <div className="text-light-muted text-sm">Presupuesto rápido</div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Detail */}
      <section className="py-20 bg-dark-bg">
        <div className="container mx-auto px-4">
          <div className="space-y-20" data-product-shop>
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                id={service.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="scroll-mt-24"
              >
                <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                  {/* Image */}
                  <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                    <div className="relative rounded-2xl overflow-hidden aspect-[4/3] w-full h-full">
                      <OptimizedImage
                        src={service.image}
                        alt={service.title}
                        loading="lazy"
                        className="w-full h-full object-cover object-top"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-dark-bg/50 to-transparent"></div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                    <div className="w-14 h-14 flex items-center justify-center bg-primary-orange/10 rounded-lg mb-6">
                      <i className={`${service.icon} text-3xl text-primary-orange`}></i>
                    </div>
                    <h2 className="text-4xl font-display font-bold mb-4">{service.title}</h2>
                    <p className="text-lg text-light-muted mb-8">{service.description}</p>

                    {/* Includes */}
                    <div className="mb-8">
                      <h3 className="text-xl font-semibold mb-4 text-primary-orange">¿Qué incluye?</h3>
                      <ul className="space-y-2">
                        {service.includes.map((item, idx) => (
                          <li key={idx} className="flex items-start">
                            <i className="ri-checkbox-circle-fill text-primary-orange mt-1 mr-3 flex-shrink-0"></i>
                            <span className="text-light-muted">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Use Cases */}
                    <div className="mb-8">
                      <h3 className="text-xl font-semibold mb-4 text-primary-orange">Casos de uso</h3>
                      <ul className="space-y-2">
                        {service.useCases.map((useCase, idx) => (
                          <li key={idx} className="flex items-start">
                            <i className="ri-arrow-right-s-fill text-primary-orange mt-1 mr-2 flex-shrink-0"></i>
                            <span className="text-light-muted">{useCase}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Price Factors */}
                    <div className="bg-dark-card p-6 rounded-xl border border-dark-lighter mb-6">
                      <h3 className="text-lg font-semibold mb-3 flex items-center">
                        <i className="ri-price-tag-3-line text-primary-orange mr-2"></i>
                        ¿Qué afecta al precio?
                      </h3>
                      <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-light-muted">
                        {service.priceFactors.map((factor, idx) => (
                          <li key={idx} className="flex items-start">
                            <i className="ri-checkbox-blank-circle-fill text-primary-orange text-xs mt-1.5 mr-2 flex-shrink-0"></i>
                            <span>{factor}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* CTA */}
                    <div className="flex flex-col sm:flex-row gap-3">
                      <a
                        href="tel:+34629501941"
                        className="flex items-center justify-center space-x-2 px-6 py-3 bg-primary-orange text-white rounded-lg font-semibold hover:shadow-glow-orange transition-all whitespace-nowrap"
                      >
                        <i className="ri-phone-line"></i>
                        <span>Llamar</span>
                      </a>
                      <a
                        href="https://wa.me/34629501941"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center space-x-2 px-6 py-3 border-2 border-primary-orange text-primary-orange rounded-lg font-semibold hover:bg-primary-orange hover:text-white transition-all whitespace-nowrap"
                      >
                        <i className="ri-whatsapp-line"></i>
                        <span>WhatsApp</span>
                      </a>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-primary-orange to-primary-orange-light">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-display font-bold mb-6 text-white">
              Presupuesto sin compromiso
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Cuéntanos tu proyecto y te daremos un presupuesto detallado y personalizado
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href="tel:+34629501941"
                className="flex items-center space-x-2 px-8 py-4 bg-white text-primary-orange rounded-lg font-semibold text-lg hover:shadow-xl transition-all whitespace-nowrap"
              >
                <i className="ri-phone-line text-xl"></i>
                <span>629 501 941</span>
              </a>
              <Link
                to="/contacto"
                className="flex items-center space-x-2 px-8 py-4 border-2 border-white text-white rounded-lg font-semibold text-lg hover:bg-white hover:text-primary-orange transition-all whitespace-nowrap"
              >
                <i className="ri-mail-line text-xl"></i>
                <span>Formulario</span>
              </Link>
            </div>
            <p className="text-white/80 mt-6 text-sm">
              Conoce más sobre <Link to="/empresa" className="text-white underline hover:text-white/90">nuestra empresa</Link> o consulta las <Link to="/faq" className="text-white underline hover:text-white/90">preguntas frecuentes</Link>
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
      <CookieBanner />
      <ScrollToTopButton />
    </div>
  );
};

export default ServiciosPage;
